<!--
     QDirectory tree view

     USAGE:
     <ExplorerDirectory
         v-on:directory-selection="myfunction(new_dirs)"
     ></ExplorerDirectory>

     It also stores in vuex through SET_FILTER_DIRECTORY

-->

<template>
  <div>
    <v-container
      fluid
      class="ma-0 pa-0"
      dense
    >
      <v-row no-gutters dense>
        <v-col cols="8">
          <v-col
            class="d-flex justify-end mb-0 pb-0 component"
            md="auto"
          >
            <SearchTextTable @input="setSearchText"/>
          </v-col> 
        </v-col>
        <v-col align="right">
          <v-btn
            fab
            color="primary"
            class="add-button-fab"
            @click="open_add"
            small
          >
            <v-icon small>mdi-plus-thick</v-icon>
          </v-btn>
        </v-col>
        <v-col align="right">
          <v-btn
            fab
            color="primary"
            class="add-button-fab"
            @click="open_edit"
            :disabled="!active.id"
            small
          >
            <v-icon small class="pr-1">
              mdi-pencil
            </v-icon>
          </v-btn>
        </v-col>
        <v-col align="right">
          <v-btn
            icon
            @click="expand_all = !expand_all"
          >
            <v-icon small class="pr-1">
              {{ expand_all ? 'mdi-collapse-all' : 'mdi-expand-all' }}
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      v-model="dialog.show"
      max-width="600px"
      @keydown.esc="close"
    >
      <v-card>
        <v-card-title class="modal-card-title">
          <span class="qmkey-modal-h1 pb-0">
            {{ dialog.title }}
          </span>
          <v-spacer></v-spacer>
          <v-btn
            class="qmkey-modal-dialog-close"
            text
            plain
            right
            small
            :ripple="false"
            @click="dialog.show = false"
          >
            <v-icon :ripple="false" small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container fluid>
            <v-row dense>
              <v-col cols="12">
                <ExplorerDirectoryPath
                  :path="active.path"
                ></ExplorerDirectoryPath>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="dialog.name"
                  label="Name"
                  outlined
                  dense
                  background-color="#f2f2f2"
                  class="qmkey-form-input required"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-select
                  v-if="!parent_hidden"
                  v-model="dialog.parent_id"
                  :items="select_directories"
                  label="Parent"
                  placeholder="Select parent folder"
                  item-text="name"
                  item-value="id"
                  class="qmkey-form-input"
                  outlined
                  dense
                  background-color="#f2f2f2"
                >
                  <template>
                    <v-icon slot="append" color="primary">
                      {{ false || 'mdi-menu-down' }}
                    </v-icon>
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col
                align="center"
                justify="center"
              >
                <v-color-picker
                  v-model="dialog.color"
                  mode="hexa"
                  :swatches="swatches"
                  show-swatches
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="qmkey-modal-dialog-card-actions">
          <v-spacer></v-spacer>
          <v-btn
            class="qmkey-modal-dialog-button-secundary"
            text
            @click="dialog.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            class="qmkey-modal-dialog-button-submit"
            text
            @click="dialog.save"
            :disabled="!valid"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-treeview
      ref="directory-tree"
      v-model="selection"
      dense
      :open="open"
      :items="qdirectory_list"
      :search="search"
      selectable
      return-object
      selection-type="independent"
      activatable
      @update:active="manage_active"
      active-class="v-treeview-node--active primary--text"
      @input="manage_selection"
    >
      <!-- To make selection-type more intuitive you can add to v-treeview:
           selection-type="unicorn"
           https://stackoverflow.com/a/66187402 >v2.4-->

      <template v-slot:prepend="{ item, open }">
        <v-icon :color="item.color" small>
          {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
        </v-icon>
      </template>

      <template slot="label" slot-scope="{ item }">
        <a
          class="black--text text-start"
        >
          {{ item.name }}
        </a>
      </template>
    </v-treeview>
  </div>
</template>


<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import QDirectory from '@/api/QDirectory'
import ExplorerDirectoryPath from '@/components/explorer/ExplorerDirectoryPath'
import SearchTextTable from '@/components/SearchTextTable'

export default {
  name: 'ExplorerDirectory',

  components: {
    ExplorerDirectoryPath,
    SearchTextTable,
  },

  data: function () {
    return {
      rename_dialog: false,
      dialog: {
        show: false,
        name: '',
        color: '',
        parent_id: '',
        save: function () {},
      },
      active: {}, // Directory
      expand_all: false,
      open: [],
      selection: [],
      search: null,
      directory_colors: [
        '#29a1c5',
        '#404040',
        '#753c8f',
        '#85a43c',
        '#acacac',
        '#ff0000',
        '#f7a42f',
        '#181cbb',
        '#ff66ff',
        '#ffff33',
      ],
      swatches: [
        ['#FF0000', '#AA0000', '#550000'],
        ['#FFFF00', '#AAAA00', '#555500'],
        ['#00FF00', '#00AA00', '#005500'],
        ['#00FFFF', '#00AAAA', '#005555'],
        ['#0000FF', '#0000AA', '#000055'],
      ],
      parent_hidden: false,
      not_again: false,
    }
  },

  computed: {
    ...mapState([
      'sitePath',
      'qdirectory',
      'qdirectory_list',
      'activeDirectory',
      'filterDirectory',
    ]),
    valid() {
      return !!this.dialog.name
    },
    select_directories() {
      let dirs = this.qdirectory
      if (dirs) {
        return Object.values(dirs).filter((d) => d.id != this.active.id)
      }
      return []
    },
  },

  mounted: function () {
    // TODO: ¿Remove me on multiple selection?
    //  this.active = this.filterDirectory[0] || {};
  },

  methods: {
    ...mapActions(['LOAD_DIRECTORY_ITEMS']),
    ...mapMutations(['SET_FILTER_DIRECTORY', 'SET_ACTIVE_DIRECTORY']),

    open_add() {
      if (this.active.id) {
        this.dialog.title = 'AÑADIR SUBCARPETA ' + this.active.name
        this.dialog.color = this.active.color
        this.dialog.parent_id = this.active.id
      } else {
        this.dialog.title = 'AÑADIR CARPETA'
        this.dialog.color = this.directory_colors[0]
        this.dialog.parent_id = ''
      }
      this.parent_hidden = true
      this.dialog.show = true
      this.dialog.name = ''

      this.dialog.save = function () {
        this.apply_add_folder()
        this.dialog.show = false
      }.bind(this)
    },

    open_edit() {
      if (!this.active.id) return // No selected directory
      let dir = this.active
      this.dialog.show = true
      this.dialog.title = 'EDITAR CARPETA'
      this.dialog.name = dir.name
      this.dialog.color = dir.color
      let parent = Object.values(this.qdirectory).filter(
        (d) => d.id == this.active.parent_id,
      )
      this.dialog.parent_id = ''
      if (parent.length > 0) {
        this.dialog.parent_id = parent[0].id
      }
      this.parent_hidden = false
      this.dialog.save = function () {
        this.apply_edit_folder()
        this.dialog.show = false
      }.bind(this)
    },

    apply_add_folder() {
      QDirectory.post(this.sitePath, {
        name: this.dialog.name,
        color: this.dialog.color,
        parent: this.active.id || null,
      })
        .then(() => this.LOAD_DIRECTORY_ITEMS(this.sitePath))
        .catch(console.warn)
    },

    apply_edit_folder() {
      QDirectory.put(this.sitePath, this.active.id, {
        name: this.dialog.name,
        color: this.dialog.color,
        parent: this.dialog.parent_id,
      })
        .then(() => this.LOAD_DIRECTORY_ITEMS(this.sitePath))
        .catch(console.warn)
    },

    setSearchText(search_text) {
      this.search = search_text
    },

    manage_active(item){
      console.log("manage_Active",item)
      this.selection = item
      this.SET_ACTIVE_DIRECTORY(item);
      this.SET_FILTER_DIRECTORY(item);
      this.active = (
        !item.length || this.active.id == item[0].id) ? {} : item[0]            
    },
    manage_selection(items){
      if (this.not_again) {
        this.not_again = false
        return
      }
      console.log('items', items)
      let include = false
      if (this.active) {
        include = this.selection.some( 
            value => {return value.id === this.active.id })
        if (!include){
          this.selection.push(this.active)
          this.not_again = true
        }
      }
        
      console.log("include", include)
      this.SET_FILTER_DIRECTORY(this.selection);
      
    }
  },

  watch: {
    expand_all(expand) {
      this.open = !expand
        ? []
        : Object.values(this.qdirectory).filter((d) => !!d.children)
    },

    //  active(post, pre) {
    //    this.SET_ACTIVE_DIRECTORY(this.active);

    //    // TODO: THIS LIMITS TO ONE SELECTED FOLDER.
    //    //       Remove this function and use the one below to unlimit
    //    this.selection = this.active.id ? [this.active] : [];
    //    this.$emit('directory-selection', this.selection);
    //    this.SET_FILTER_DIRECTORY(this.selection);
    //  },
    enable_button: function () {
      this.valid = this.dialog.name ? true : false
    },
    selection(post, pre) {
      console.log('post', post)
      console.log('pre', pre)
      // get node clicked
      // let difference = post
      //            .filter(x => !pre.includes(x))
      //            .concat(pre.filter(x => !post.includes(x)));
      // if (list.hasOwnProperty(x) && list[x] === obj)      
    }
    // Multi Checkbox selection:
    // selection(post, pre) {
    //   this.$emit('directory-selection', this.selection);
    //   this.SET_FILTER_DIRECTORY(this.selection);
    // },

    // Vuetify Tree-view --- Get parent node along with the children node
    // https://stackoverflow.com/questions/60776489/vuetify-tree-view-get-parent-node-along-with-the-children-node
    // selection(post, pre) {
    //   console.log('post', post)
    //   let _selectedNodes = []
    //   console.log('this.selection', this.selection)
    //   let _treenodes = this.$refs['directory-tree'].nodes
    //   // console.log("this.selection", this.$refs['directory-tree'].selected)
    //   for (const key in _treenodes) {
    //     if (Object.hasOwnProperty.call(_treenodes, key)) {
    //       const node = _treenodes[key]
    //       if (node.isSelected) _selectedNodes.push(node.item)
    //     }
    //   }
    //   this.selectionWithParents = _selectedNodes
    //   this.$emit('directory-selection', this.selectionWithParents)
    //   this.SET_FILTER_DIRECTORY(this.selectionWithParents)
  },
}
</script>
<style lang="scss">
@import '@/sass/explorer.scss';
@import '@/sass/modalform.scss';
</style>